"use client";

import React, { FC, useEffect, useMemo } from "react";
import OnboardingTutorial from "@/components/shared/OnboardingTutorial";
import { useAchievementToast } from "@/components/shared/AchievementToast";
import MobileLayout from "@/components/shared/MobileLayout";
import ChallengePathNavigator from "@/components/shared/ChallengePathNavigator";
import { useAuth } from "@/hooks/useAuth";
import FeedbackWidget from "@/components/shared/FeedbackWidget";
import { analytics } from "@/lib/analytics";
import { BadgeEarnedModal } from "@/components/shared/BadgeEarnedModal";
import {
  authNavigationTabs,
  notAuthNavigationTabs,
} from "@/utils/desktopNavigation";
import { useAppStore } from "@/store/useAppStore";
import { TikTokMode } from "@/components/shared/TikTokMode";
import { Header } from "./Header";
import { Navigation } from "./Navigation";
import { SocialChallengeModal } from "./SocialChallengeModal";
import { ShareResultsModal } from "./modals/ShareResultsModal";
import { AuthModal } from "./modals/AuthModal";
import { EmailConfimationModal } from "./modals/EmailConfimationModal";
import { getUserProfile } from "@/lib/user/getUserProfile";
import { createUserProfile } from "@/lib/user/createUserProfile";
import { getUserPredictions } from "@/lib/predictions/getUserPredictions";
import { getUserBadges } from "@/lib/badges/getUserBadges";
import { liveMarkets } from "@/utils/liveMarkets";
import { getUserCompletedChallenges } from "@/lib/challenge/getUserCompletedChallanges";
import { challengePaths } from "@/lib/challengePaths";
import { organizeCompletedChallengesByPath } from "@/helpers/organizeCompletedChallengeByPath";
import { Footer } from "./Footer";
import { toast } from "react-toastify";

interface Props {
  children: React.ReactNode;
}

export const AppWrapper: FC<Props> = ({ children }) => {
  const { user } = useAuth();
  const {
    activeModal,
    setBadges,
    setMintedBadges,
    setCrowdBeats,
    setPredictions,
    setStreak,
    setUserScore,
    setUserRank,
    setCompletedMarkets,
    setCompletedChallenges,
  } = useAppStore();

  const { AchievementToastComponent } = useAchievementToast();

  useEffect(() => {
    analytics.init();
  }, []);


  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user?.id && user?.email) {
        try {
          const profile = await getUserProfile(user.id);

          if (!profile) {
            await createUserProfile(user.id, user.email);
          } else {
            setUserScore(profile.score);
            setStreak(profile.streak);
            setCrowdBeats(profile.crowd_beats);

            profile.rank && setUserRank(profile.rank);
          }
        } catch (error) {
          toast.error("Failed to fetch user profile");
          console.log("Error: ", error);
        }
      } else {
        setUserScore(0);
        setStreak(0);
        setCrowdBeats(0);
        setUserRank(null);
      }
    };

    fetchUserProfile();
  }, [user?.id, user?.email]);

  useEffect(() => {
    const fetchUserPredictions = async () => {
      if (user?.id) {
        try {
          const predictions = await getUserPredictions(user.id);

          const alreadyCompletedMarketIds = liveMarkets
            .filter((market) =>
              predictions.some(
                (prediction: any) => prediction.question === market.question
              )
            )
            .map((market) => market.id);

          setPredictions(predictions);
          setCompletedMarkets(alreadyCompletedMarketIds);
        } catch (error) {
          toast.error("Failed to fetch predictions");
          console.log("Error: ", error);
        }
      } else {
        setPredictions([]);
        setCompletedMarkets([]);
      }
    };
    fetchUserPredictions();
  }, [user?.id, liveMarkets]);

  useEffect(() => {
    const fetchUserBadges = async () => {
      if (user?.id) {
        try {
          const userBadges = await getUserBadges(user.id);
          const badgeIds = userBadges.map((badge) => badge.badge_id);
          const mintedBadges = userBadges.filter((badge) => !!badge.minted);
          const mintedBadgesIds = mintedBadges.map(
            (mintedBadge) => mintedBadge.badge_id
          );
          setBadges(badgeIds);
          setMintedBadges(mintedBadgesIds);
        } catch (error) {
          toast.error("Failed to fetch badges");
          console.log("Error: ", error);
        }
      } else {
        setBadges([]);
        setMintedBadges([]);
      }
    };

    fetchUserBadges();
  }, [user?.id]);

  useEffect(() => {
    const fetchCompletedChallenges = async () => {
      if (user?.id) {
        try {
          const dbCompletedChallenges = await getUserCompletedChallenges(
            user.id
          );
          const completedChallengesIds = dbCompletedChallenges.map(
            (challenge: any) => challenge.challenge_id
          );
          setCompletedChallenges(completedChallengesIds);

          const pathData = organizeCompletedChallengesByPath(
            dbCompletedChallenges,
            user.id
          );
          console.log(
            "Path data organized and saved to localStorage:",
            pathData
          );
        } catch (error) {
          console.error("Failed to fetch completed challenges:", error);
          toast.error("Failed to fetch completed challenges");
        }
      } else {
        setCompletedChallenges([]);
        challengePaths.forEach((path) => {
          localStorage.removeItem(`path_${path.id}_${user?.id}`);
        });
      }
    };

    fetchCompletedChallenges();
  }, [user?.id]);


  return (
    <div className="min-h-screen h-full bg-black text-white">
      <Header />
      <div className="block xl:hidden">
        <Navigation />
      </div>
      {children}
      <BadgeEarnedModal />
      <div style={{ display: "none" }} aria-hidden="true">
        <ChallengePathNavigator />
      </div>
      {activeModal === "challenge" && <SocialChallengeModal />}
      {activeModal === "share" && <ShareResultsModal />}
      {activeModal === "auth" && <AuthModal />}
      {activeModal === "email" && <EmailConfimationModal />}
      <OnboardingTutorial />
      <AchievementToastComponent />
      <TikTokMode />
      <FeedbackWidget />
      <Footer />
    </div>
  );
};
